import React, { useCallback, useRef, useState } from 'react'

import { Editor } from '@tinymce/tinymce-react'
import { debounce } from 'lodash'
import { Flex, Text } from 'stardust'
import styled from 'styled-components'
import { Editor as EditorClass, EditorEvent, TinyMCE } from 'tinymce'

import config from '~/config'

import colors from './tokens/colors'

const Container = styled(Flex)`
  > div.tox.tox-tinymce {
    flex-grow: 1;
    div.tox-toolbar__group {
      position: relative;
      :not(:last-of-type)::after {
        content: '';
        position: absolute;
        height: 20px;
        width: 2px;
        background-color: ${colors.cosmicShade4};
        border-radius: 1px;
        right: -1px;
      }
    }
    .tox-editor-header {
      padding: 0px;
      box-shadow: none;
      border-bottom: 2px solid ${colors.cosmicShade4};
    }
    .tox-tbtn:focus:after,
    .tox-number-input button:focus::after,
    .tox-split-button:focus::after {
      box-shadow: 0 0 0 2px ${colors.nebulaBlue5};
    }
    .tox-tbtn--active,
    .tox-tbtn--enabled,
    .tox-tbtn--enabled:focus,
    .tox-tbtn--enabled:hover,
    .tox-tbtn:active {
      background-color: ${colors.nebulaBlue0};
    }
    .tox-number-input button:active {
      background-color: ${colors.nebulaBlue0};
    }
    .tox-edit-area::before {
      border-color: ${colors.nebulaBlue5};
      border-radius: 8px;
      body {
        caret-color: ${colors.nebulaBlue5};
      }
    }
  }
`

type EditorOptions = Parameters<TinyMCE['init']>[0]
type TinyMCEInitProps = EditorOptions &
  Partial<Record<'selector' | 'target' | 'readonly' | 'license_key', undefined>>

interface TinyMCEEditorProps {
  value?: string
  placeholder?: string
  onChange?: (plainText: string, richText: string) => void
  maxLength?: number
}

const TinyMCEEditor = ({ value, placeholder, onChange, maxLength }: TinyMCEEditorProps) => {
  const [lengthValidation, setLengthValidation] = useState<
    | undefined
    | {
        msg: string
        valid: boolean
      }
  >(undefined)
  const editorRef = useRef<EditorClass | null>(null)
  const initialValue = useRef(value)

  const validateDescriptionLength = useCallback((maxLength: number, descriptionLength: number) => {
    return (() => {
      if (descriptionLength >= maxLength) {
        return { valid: false, msg: '0 characters left' }
      } else {
        return { valid: true, msg: `${descriptionLength}/${maxLength}` }
      }
    })()
  }, [])

  const handleKeyPress = (event: EditorEvent<KeyboardEvent>) => {
    if (!editorRef.current || !maxLength) return
    const currentLength = editorRef.current.getContent({ format: 'text' }).length
    const selectedText = editorRef.current.selection.getContent({ format: 'text' })

    if (
      currentLength >= maxLength &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !selectedText.length
    ) {
      event.preventDefault() // Prevent the new character from being added
    }
  }

  const handleUpdate = debounce((richText: string, editor: EditorClass) => {
    if (!onChange) return
    const plainText = editor.getContent({ format: 'text' })
    if (maxLength) {
      const wordcount = editor.plugins.wordcount
      const charecterCount = wordcount.body.getCharacterCount()
      const { msg, valid } = validateDescriptionLength(maxLength, charecterCount)
      if (valid || lengthValidation?.valid) onChange(plainText, richText)
      setLengthValidation({ msg, valid })
      return
    }
    onChange(plainText, richText)
  }, 500)

  return (
    <Container flexGrow={1} flexDirection="column">
      <Editor
        initialValue={initialValue.current}
        init={{
          ...defaultOptions,
          placeholder,
          ...(maxLength // prevent pasting content more that max limit
            ? {
                paste_preprocess: (editor, args) => {
                  const currentLength = editor.getContent({ format: 'text' }).length || 0
                  const selectedText = editor.selection.getContent({ format: 'text' })
                  const remainingSpace = maxLength - currentLength + selectedText.length

                  // Check if the pasted content exceeds the remaining space
                  if (args.content.length > remainingSpace) {
                    // Slice the pasted content to fit within the remaining space
                    const slicedContent = args.content.slice(0, remainingSpace)
                    // Update the args.content with the sliced content
                    args.content = slicedContent
                  }
                },
              }
            : {}),
        }}
        onInit={(_evt, editor) => {
          editorRef.current = editor
          editor.on('keydown', handleKeyPress)
          if (maxLength) {
            const wordcount = editor.plugins.wordcount
            const charecterCount = wordcount.body.getCharacterCount()
            setLengthValidation(validateDescriptionLength(maxLength, charecterCount))
          }
        }}
        onEditorChange={handleUpdate}
        licenseKey={config.TINYMCE_KEY}
        tinymceScriptSrc={`${window.location.origin}/tinymce/tinymce.min.js`}
      />
      {maxLength ? (
        <Flex px={2} py={1}>
          {!lengthValidation?.valid ? (
            <Text color={colors.cosmicShade20} fontSize="14px" fontWeight={400} lineHeight="21px">
              You have reached the maximum character limit of 15,000
            </Text>
          ) : null}
          <Text ml="auto" lineHeight="21px" fontSize="14px" color={colors.cosmicShade11}>
            {lengthValidation?.msg}
          </Text>
        </Flex>
      ) : null}
    </Container>
  )
}

TinyMCEEditor.displayName = 'TinyMCEEditor'
export default React.memo(TinyMCEEditor)

const defaultOptions: TinyMCEInitProps = {
  width: '100%',
  height: '191px', //minimum height to maintain responsiveness
  content_style: `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap');
    body { 
    font-size: 14px;
      font-family: Inter, sans-serif;
    }
  `,
  menubar: false,
  statusbar: true,
  elementpath: false,
  resize: false,
  images_file_types: 'jpg,svg,webp',
  block_unsupported_drop: true,
  plugins: [
    'advlist',
    'autolink',
    'checklist',
    'editimage',
    'emoticons',
    'link',
    'lists',
    'powerpaste',
    'wordcount',
    // 'tinymcespellchecker',
  ],
  toolbar:
    'undo redo | bold italic underline strikethrough | forecolor backcolor | fontsizeinput fontfamily | align bullist numlist | emoticons hr wordcount', // quickimage
  quickbars_selection_toolbar: false,
  quickbars_insert_toolbar: false,
  quickbars_image_toolbar: 'alignleft aligncenter alignright',
  table_toolbar:
    'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
  contextmenu: false,
  link_context_toolbar: false,
  link_default_target: '_blank',
  editimage_toolbar: 'rotateleft rotateright | flipv fliph | editimage imageoptions',
  typography_default_lang: ['en-US'],
  spellchecker_language: 'en',
  // spellchecker_rpc_url: `${window.location.origin}/tinymce/tinymce-services/`,
  noneditable_class: 'mceNonEditable',
  forced_root_block: '',
  force_br_newlines: true,
  force_p_newlines: true,
  paste_data_images: false,
  paste_block_drop: false,
  smart_paste: false,
  branding: false,
  toolbar_mode: 'sliding',
  toolbar_sticky: true,
  toolbar_sticky_offset: 84,
  font_size_input_default_unit: 'px',
}
